import { Component, reactive, computed, watch } from 'vue'
import { batchPaymentTermsString, BatchSearchDto, BatchSearchSyncTarget } from '@/models/batch'
import { formatDateTime } from '@/utils/date-formatting'
import { useGetters } from 'vuex-composition-helpers'
import { GetterTree } from 'vuex'
import { Details as DetailsCell, LocationJobColumn } from '@/components/jobs/cells'
import BatchSyncStatusChip from '@/components/batches/BatchSyncStatusChip.vue'
import BatchExternalLink from './BatchExternalLink.vue'
import { SyncTargetSchema, useAllSyncTargetSchemasQuery } from '@/api/schemas'
import { useBatchColumnStore } from './useBatchColumnStore'
import { useCurrencyFormatter } from '@/utils/number-formatting'

export type ColumnHeader<K extends keyof BatchSearchDto = keyof BatchSearchDto> = {
  selector: string
  text: string
  value: K
  /**
   * When set, this column is only shown when an installed sync-target app
   * matches this type string (e.g. 'qbo').  Omit for columns that are always
   * visible regardless of installed integrations.
   */
  appType?: string
  formatter?: (value: BatchSearchDto[K]) => any
  component?: Component
}

export type ColumnHeaders = {
  [K in keyof BatchSearchDto]: ColumnHeader<K>
}[keyof BatchSearchDto]

function column<K extends keyof BatchSearchDto>(col: ColumnHeader<K>): ColumnHeaders {
  return col as ColumnHeaders
}

interface RootGetters extends GetterTree<any, any> {
  'companies/getByOrganizationId': () => (orgId: string) => { name: string } | null
  'users/getBySid': () => (userSid: string) => { name: string } | null
  'companies/getById': () => (id: number) => { name: string } | null
  'businessUnits/getBusinessUnitById': () => (id: number) => { name: string } | null
  'permissions/isRateViewer': () => boolean
}

/** Maps sync target app type to the property name on InvoiceSearchDto */
const syncTargetDtoMap: Record<string, 'qboSync' | ''> = {
  qbo: 'qboSync',
}

interface LookupGetters {
  getUserBySid: (sid: string) => { name: string } | null
  getCompanyById: (id: number) => { name: string } | null
  getBusinessUnitById: (id: number) => { name: string } | null
}

function buildCustomDataColumns(
  schemas: SyncTargetSchema[],
  getters: LookupGetters,
): ColumnHeaders[] {
  // Collect all field titles across sync targets to detect overlaps
  const titleCounts = new Map<string, number>()
  for (const schema of schemas) {
    const properties = schema.schema?.schema?.properties
    if (!properties) continue
    if (!syncTargetDtoMap[schema.type]) continue

    for (const prop of Object.values(properties)) {
      if (!prop.title) continue
      titleCounts.set(prop.title, (titleCounts.get(prop.title) ?? 0) + 1)
    }
  }

  const columns: ColumnHeader[] = []

  for (const schema of schemas) {
    const dtoProperty = syncTargetDtoMap[schema.type]
    if (!dtoProperty) continue

    const properties = schema.schema?.schema?.properties
    if (!properties) continue

    for (const [fieldKey, fieldDef] of Object.entries(properties)) {
      if (!fieldDef.title) continue
      // Skip complex types that don't render well as table cells
      if (fieldDef.type === 'object' || fieldDef.type === 'array') continue

      const needsPrefix = (titleCounts.get(fieldDef.title) ?? 0) > 1
      const text = needsPrefix ? `${schema.name} ${fieldDef.title}` : fieldDef.title

      const baseFormatter = (syncTarget: BatchSearchSyncTarget | null) =>
        syncTarget?.customData?.[fieldKey] ?? null

      const lookupEntity = fieldDef.lookup?.entity
      let formatter: (syncTarget: BatchSearchSyncTarget | null) => any = baseFormatter
      let component: Component | undefined

      if (lookupEntity === 'location') {
        component = LocationJobColumn
      } else if (lookupEntity === 'user') {
        formatter = (syncTarget) => {
          const value = syncTarget?.customData?.[fieldKey]
          return value ? (getters.getUserBySid(value)?.name ?? value) : null
        }
      } else if (lookupEntity === 'company') {
        formatter = (syncTarget) => {
          const value = syncTarget?.customData?.[fieldKey]
          return value ? (getters.getCompanyById(value)?.name ?? value) : null
        }
      } else if (lookupEntity === 'business_unit') {
        formatter = (syncTarget) => {
          const value = syncTarget?.customData?.[fieldKey]
          return value ? (getters.getBusinessUnitById(value)?.name ?? value) : null
        }
      }

      const col = column({
        selector: `${schema.type}_custom_${fieldKey}`,
        text,
        value: dtoProperty,
        formatter,
        ...(component ? { component } : {}),
      }) as ColumnHeader<keyof BatchSearchDto>

      columns.push(col)
    }
  }

  return columns
}

export function useOrderedBatchColumns() {
  const {
    'users/getBySid': getUserBySid,
    'companies/getById': getCompanyById,
    'businessUnits/getBusinessUnitById': getBusinessUnitById,
  } = reactive(
    useGetters<RootGetters>([
      'users/getBySid',
      'companies/getById',
      'businessUnits/getBusinessUnitById',
    ]),
  )
  const { data: syncTargetSchemas } = useAllSyncTargetSchemasQuery()
  const { 'permissions/isRateViewer': isRateViewer } = useGetters<RootGetters>([
    'permissions/isRateViewer',
  ])

  const columns: ColumnHeaders[] = [
    column({
      selector: 'BatchNumber',
      text: 'Batch Number',
      value: 'batchNumber',
    }),
    column({
      selector: 'customerName',
      text: 'Customer',
      value: 'customerName',
    }),
    column({
      selector: 'jobCount',
      text: 'Job Count',
      value: 'jobCount',
    }),
    column({
      selector: 'subtotal',
      text: 'Subtotal',
      value: 'subtotal',
      formatter: (subtotal) => useCurrencyFormatter().format(subtotal),
    }),
    column({
      selector: 'QboSync.Status',
      text: 'Qbo Sync Status',
      value: 'qboSync',
      appType: 'qbo',
      formatter: (value) => value?.status,
      component: BatchSyncStatusChip,
    }),
    column({
      selector: 'QboSync.LastSyncedAt',
      text: 'Qbo Synced At',
      value: 'qboSync',
      appType: 'qbo',
      formatter: (value) => value?.lastSyncedAt && formatDateTime(value?.lastSyncedAt),
    }),
    column({
      selector: 'QboSync.SyncedBy',
      text: 'Qbo Synced By',
      value: 'qboSync',
      appType: 'qbo',
      formatter: (value) => value?.initiatedByUser,
    }),
    column({
      selector: 'QboSync.ExternalLink',
      text: 'Qbo Invoice Link',
      value: 'qboSync',
      appType: 'qbo',
      formatter: (value) => value?.externalInvoice,
      component: BatchExternalLink,
    }),
    column({
      selector: 'creator',
      text: 'Creator',
      value: 'createdBy',
      formatter: (value) => value && getUserBySid(value)?.name,
    }),
    column({
      selector: 'createDate',
      text: 'Create Date',
      value: 'created',
      formatter: (value) => value && formatDateTime(value),
    }),
    column({
      selector: 'batchDate',
      text: 'Batch Date',
      value: 'batchDate',
      formatter: (value) => value && formatDateTime(value),
    }),
    column({
      selector: 'dueDate',
      text: 'Due Date',
      value: 'dueDate',
      formatter: (value) => value && formatDateTime(value),
    }),
    column({
      selector: 'memo',
      text: 'Memo',
      value: 'memo',
      component: DetailsCell,
    }),
  ]

  const columnStore = useBatchColumnStore()

  /** All columns (core + custom from sync target schemas), filtered by installed apps. */
  const allColumns = computed(() => {
    const installedAppTypes = new Set((syncTargetSchemas.value ?? []).map((s) => s.type))

    const visibleStaticColumns = columns.filter((c) => {
      if (c.appType && !installedAppTypes.has(c.appType)) return false
      if (!isRateViewer.value && c.selector === 'subtotal') return false
      return true
    })

    const customDataColumns = buildCustomDataColumns(syncTargetSchemas.value ?? [], {
      getUserBySid,
      getCompanyById,
      getBusinessUnitById,
    })
    return [...visibleStaticColumns, ...customDataColumns]
  })

  const coreColumnSelectors = computed(() => {
    const installedAppTypes = new Set((syncTargetSchemas.value ?? []).map((s) => s.type))
    return columns
      .filter((c) => {
        if (c.appType && !installedAppTypes.has(c.appType)) return false
        if (!isRateViewer.value && c.selector === 'subtotal') return false
        return true
      })
      .map((c) => c.selector)
  })

  // (Re-)initialize the store whenever the full column set changes,
  // but only once sync-target schemas have finished loading so we don't
  // reset with an incomplete column list.
  watch(
    allColumns,
    (cols) => {
      if (syncTargetSchemas.value === undefined) return
      columnStore.initialize(
        cols.map((c) => c.selector),
        coreColumnSelectors.value,
      )
    },
    { immediate: true },
  )

  /** Visible + ordered headers for the table. */
  const headers = computed(() => {
    const all = allColumns.value
    const ordering = columnStore.columnOrdering
    const visible = columnStore.visibleColumns

    // Build an ordered list, then filter to visible only
    const ordered: typeof all = []
    for (const sel of ordering) {
      const col = all.find((c) => c.selector === sel)
      if (col) ordered.push(col)
    }
    // Append any columns not yet in the ordering
    for (const col of all) {
      if (!ordering.includes(col.selector)) {
        ordered.push(col)
      }
    }

    return ordered.filter((c) => visible.includes(c.selector))
  })

  return {
    allColumns,
    headers,
  }
}
